import React, { useCallback } from 'react';
import { useDropzone } from 'react-dropzone';
import { FileTextIcon, UploadIcon, CheckIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';
import { FileThumbnail } from '../shared/FileThumbnail';
import { useFilePreview } from '../shared/useFilePreview';
import { formatFileSize, getFileTypeLabel, getFileType } from '../shared/fileUtils';
import { FilePreviewButton } from '../shared/preview';

const DEFAULT_ACCEPTED_TYPES: Record<string, string[]> = {
  'application/pdf': [],
  'image/jpeg': [],
  'image/png': [],
  'application/msword': [],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': [],
};

export type FileUploaderProps = {
  /** Array of files (typically one unless `multiple` is true). */
  files: File[] | undefined;
  /** Called when files change. */
  onChange: (files: File[]) => void;
  /** MIME types to accept. */
  acceptedFileTypes?: Record<string, string[]>;
  /** Label shown in empty state. */
  label: string;
  /** Icon key for empty state (legacy). Ignored when `emptyIcon` is provided. @default 'upload' */
  icon?: 'file' | 'upload';
  /** Allow selecting multiple files. @default false */
  multiple?: boolean;

  // ─── New optional props ───────────────────────────────

  /** Visual variant. @default 'compact' */
  variant?: 'compact' | 'default';
  /** Color status when a file is selected. @default 'neutral' */
  status?: 'neutral' | 'success' | 'error';
  /** Small message shown under file info (e.g. "File selected"). */
  statusMessage?: string;
  /** Description text in empty state (default variant only). */
  description?: string;
  /** Hint text below description (e.g. "Supports JPG, PNG, PDF"). */
  hint?: string;
  /** Custom icon node for the empty state. Overrides `icon` prop. */
  emptyIcon?: React.ReactNode;
  /** Additional wrapper className. */
  className?: string;
  /** URLs of already-uploaded files to preview when no new File objects are selected (edit mode). */
  existingUrls?: string[];
  /** Disable the dropzone (no clicks / drops accepted). */
  disabled?: boolean;
};

const filledStatusStyles = {
  neutral: {
    border: 'border-gray-300',
    bg: 'bg-white',
    hoverBg: 'hover:bg-gray-50/50',
    text: 'text-gray-500',
  },
  success: {
    border: 'border-emerald-300',
    bg: 'bg-emerald-50/50',
    hoverBg: 'hover:bg-emerald-50',
    text: 'text-emerald-600',
  },
  error: {
    border: 'border-red-300',
    bg: 'bg-red-50/50',
    hoverBg: 'hover:bg-red-50',
    text: 'text-red-500',
  },
};

const EmptyIcon: React.FC<{
  icon: 'file' | 'upload';
  emptyIcon?: React.ReactNode;
  variant: 'compact' | 'default';
}> = ({ icon, emptyIcon, variant }) => {
  if (emptyIcon) {
    return variant === 'default' ? (
      <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10">
        {emptyIcon}
      </div>
    ) : (
      <>{emptyIcon}</>
    );
  }

  const IconComponent = icon === 'file' ? FileTextIcon : UploadIcon;

  if (variant === 'default') {
    return (
      <div className="flex items-center justify-center size-10 rounded-lg bg-primary/10">
        <IconComponent className="size-5 text-primary" />
      </div>
    );
  }

  return <IconComponent className="size-5" />;
};

const MultiFileItem: React.FC<{ file: File; onRemove: () => void }> = ({ file, onRemove }) => {
  const blobUrl = useFilePreview(file);
  const fileType = getFileType(file);
  return (
    <div className="relative group">
      <div className="size-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
        {blobUrl ? (
          <FileThumbnail src={blobUrl} type={fileType} size="lg" />
        ) : (
          <div className="flex items-center justify-center size-full">
            <FileTextIcon className="size-6 text-gray-400" />
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={e => {
          e.stopPropagation();
          onRemove();
        }}
        className="absolute -top-1.5 -end-1.5 size-5 rounded-full bg-red-500 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-xs leading-none">
        ×
      </button>
    </div>
  );
};

export const FileUploader = ({
  files,
  onChange,
  acceptedFileTypes = DEFAULT_ACCEPTED_TYPES,
  label,
  icon = 'upload',
  multiple = false,
  variant = 'compact',
  status = 'neutral',
  statusMessage,
  description,
  hint,
  emptyIcon,
  className,
  existingUrls,
  disabled = false,
}: FileUploaderProps) => {
  const file = files?.[0] ?? null;
  const hasNewFiles = !!files && files.length > 0;
  const effectiveExistingUrls = existingUrls?.filter(u => !!u) ?? [];

  const onDrop = useCallback(
    (accepted: File[]) => {
      if (multiple) {
        onChange([...(files ?? []), ...accepted]);
      } else {
        onChange(accepted);
      }
    },
    [onChange, files, multiple],
  );

  const removeFile = useCallback(
    (index: number) => {
      const next = [...(files ?? [])];
      next.splice(index, 1);
      onChange(next);
    },
    [files, onChange],
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: acceptedFileTypes,
    multiple,
    disabled,
  });

  const blobUrl = useFilePreview(file);
  const hasFile = !!file;
  const fileType = file ? getFileType(file) : 'unknown';
  const statusStyle = filledStatusStyles[status];

  // Preview source. Prefer the freshly-dropped File (renderable as a
  // blob URL) over any pre-existing CDN url, so the user always
  // previews what they're about to save — not what was on disk
  // before. `imageWithUploadSchema` semantics: a doc may carry a
  // `url` (already-uploaded) and the form layer may simultaneously
  // have a new `upload` (base64 not yet persisted). In that overlap
  // window the new file is the source of truth.
  const previewSrc = blobUrl ?? effectiveExistingUrls[0];

  // ──────────────────────── Compact variant ────────────────────────

  if (variant === 'compact') {
    return (
      <div
        {...getRootProps()}
        className={cn(
          'border border-dashed rounded-lg px-3 py-[9px] cursor-pointer transition-colors',
          isDragActive
            ? 'border-primary bg-primary/5'
            : hasFile
            ? cn(statusStyle.border, statusStyle.bg, statusStyle.hoverBg)
            : 'border-gray-300 hover:border-primary',
          className,
        )}>
        <input {...getInputProps()} />

        {hasFile ? (
          <div className="flex items-center gap-2">
            {blobUrl && <FileThumbnail src={blobUrl} type={fileType} size="sm" />}
            <span className="text-sm text-gray-700 truncate flex-1">{file.name}</span>
            <span className="text-xs text-gray-400 shrink-0">
              {getFileTypeLabel(file)} · {formatFileSize(file.size)}
            </span>
            <FilePreviewButton src={previewSrc} showLabel />
          </div>
        ) : !hasNewFiles && effectiveExistingUrls.length > 0 ? (
          <div className="flex items-center gap-2">
            <FileThumbnail src={effectiveExistingUrls[0]} size="sm" />
            <span className="text-sm text-gray-500 truncate flex-1">{label}</span>
            <FilePreviewButton src={previewSrc} showLabel />
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-primary">
            <EmptyIcon icon={icon} emptyIcon={emptyIcon} variant="compact" />
            <span className="subtitle2">{label}</span>
          </div>
        )}
      </div>
    );
  }

  // ──────────────────────── Default variant ─────────────────────────

  if (multiple && hasNewFiles) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="flex flex-wrap gap-2">
          {files!.map((f, idx) => (
            <MultiFileItem key={idx} file={f} onRemove={() => removeFile(idx)} />
          ))}
        </div>
        <div
          {...getRootProps()}
          className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 cursor-pointer text-primary hover:border-primary transition-colors">
          <input {...getInputProps()} />
          <UploadIcon className="size-4" />
          <span className="text-sm font-medium">{label}</span>
        </div>
      </div>
    );
  }

  if (multiple && !hasNewFiles && effectiveExistingUrls.length > 0) {
    return (
      <div className={cn('flex flex-col gap-2', className)}>
        <div className="flex flex-wrap gap-2">
          {effectiveExistingUrls.map((url, idx) => (
            <div
              key={idx}
              className="size-20 rounded-lg overflow-hidden border border-gray-200 bg-gray-50">
              <FileThumbnail src={url} size="lg" />
            </div>
          ))}
        </div>
        <div
          {...getRootProps()}
          className="flex items-center justify-center gap-2 rounded-lg border border-dashed border-gray-300 px-3 py-2 cursor-pointer text-primary hover:border-primary transition-colors">
          <input {...getInputProps()} />
          <UploadIcon className="size-4" />
          <span className="text-sm font-medium">{label}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      {...getRootProps()}
      className={cn(
        'relative flex flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed p-6 cursor-pointer transition-all duration-200',
        isDragActive
          ? 'border-primary bg-primary/5 scale-[1.01]'
          : hasFile
          ? cn(statusStyle.border, statusStyle.bg, statusStyle.hoverBg)
          : 'border-gray-200 bg-gray-50/30 hover:border-primary/40 hover:bg-gray-50',
        className,
      )}>
      <input {...getInputProps()} />

      {hasFile ? (
        <div className="flex items-center gap-3 w-full">
          {blobUrl && <FileThumbnail src={blobUrl} type={fileType} size="lg" />}
          <div className="flex flex-col gap-0.5 min-w-0 flex-1">
            <p className="text-sm font-medium text-gray-800 truncate">{file.name}</p>
            <p className="text-xs text-gray-400">
              {getFileTypeLabel(file)} · {formatFileSize(file.size)}
            </p>
            {statusMessage && (
              <p className={cn('text-xs font-medium flex items-center gap-1', statusStyle.text)}>
                {status === 'success' && <CheckIcon className="size-3" />}
                {statusMessage}
              </p>
            )}
          </div>
          <FilePreviewButton src={previewSrc} showLabel />
        </div>
      ) : !hasNewFiles && effectiveExistingUrls.length > 0 ? (
        <div className="flex items-center gap-3 w-full">
          <FileThumbnail src={effectiveExistingUrls[0]} size="lg" />
          <p className="text-sm text-gray-500 truncate flex-1">{label}</p>
          <FilePreviewButton src={previewSrc} showLabel />
        </div>
      ) : (
        <>
          <EmptyIcon icon={icon} emptyIcon={emptyIcon} variant="default" />
          <div className="flex flex-col items-center gap-1 text-center">
            <p className="text-sm font-medium text-gray-700">{description || label}</p>
            {hint && <p className="text-xs text-gray-400">{hint}</p>}
          </div>
        </>
      )}
    </div>
  );
};
